/* eslint-disable max-depth */
import React, { useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';

import { Button } from '@ui-library/button';
import { Flex } from '@ui-library/flex';
import { H4, SmallText } from '@ui-library/typography';
import { Alert } from '@ui-library/alerts';
import { Loader } from '@ui-library/loader';
import useStores from '@stores/useStores';
import LocalStorage from '@utils/localStorage';
import { memoryStore } from '@lib/memoryStore';
import { apiAuthCaller } from '@services/apiCaller';

import { McpAuthorizationCard } from './mcpAuthrizationCard';
import { OAuthParams, REQUIRED_PARAMS } from './types';
import { buildCognitoOauthUrl, buildCustomOauthUrl, validateRequiredOauthParams } from './utils';
import { usePasswordPrompt } from './usePasswordPrompt';

const ERROR_MESSAGE = 'An error occurred while processing your request.';

export const McpAuthorizationPage: React.FC = () => {
  const history = useHistory();
  const { userStore } = useStores();
  const [error, setError] = useState('');
  const [oauthParams, setOauthParams] = useState<OAuthParams | Record<string, never>>({});
  const [isProcessing, setIsProcessing] = useState(() =>
    Boolean(LocalStorage.get('postLoginParams')),
  );

  const { toggleShow, modalPlaceholder } = usePasswordPrompt({
    onSubmit: (password) =>
      apiAuthCaller({ nonWorkspaceRoute: true })
        .post(buildCustomOauthUrl(oauthParams as OAuthParams), {
          password,
        })
        .then((response) => {
          setIsProcessing(true);
          window.location.replace(response.data.redirect_uri);
        })
        .catch((e) => {
          setError(ERROR_MESSAGE);
          throw e;
        }),
  });

  useEffect(() => {
    const localStorageOauthParams = LocalStorage.get('postLoginParams');

    // try to load params from localStorage
    if (localStorageOauthParams) {
      try {
        const parsed = JSON.parse(localStorageOauthParams);
        const { params, missing } = validateRequiredOauthParams(parsed);

        // when localStorageOauthParams are present and valid, redirect to the
        // authorization URL to complete the mcp oauth
        if (missing.length === 0) {
          if (userStore.provider) {
            window.location.replace(buildCognitoOauthUrl(params, userStore.provider));
          } else {
            // We were redirected here from a username and password login
            apiAuthCaller({ nonWorkspaceRoute: true })
              .post(buildCustomOauthUrl(params), {
                password: memoryStore.getItem('password'),
              })
              .then((response) => {
                window.location.replace(response.data.redirect_uri);
              })
              .catch(() => {
                setError(ERROR_MESSAGE);
                setIsProcessing(false);
                memoryStore.removeItem('password');
              });
          }
        } else {
          setError(missing.join(', '));
          setIsProcessing(false);
        }
      } catch {
        setError(`Missing required OAuth parameters: ${REQUIRED_PARAMS.join(', ')}`);
        setIsProcessing(false);
      } finally {
        // Something went wrong, remove the params from localStorage
        LocalStorage.remove('postLoginParams');
      }
      // try to load params from the query params
      //
    } else if (window.location.search) {
      try {
        const { params, missing } = validateRequiredOauthParams(window.location.search);

        setOauthParams(params);
        setError(missing.join(', '));
      } catch {
        setError(`Missing required OAuth parameters: ${REQUIRED_PARAMS.join(', ')}`);
      }
    } else {
      setError(`Missing required OAuth parameters: ${REQUIRED_PARAMS.join(', ')}`);
    }
  }, []);

  const handleAuthorize = () => {
    // when not logged in, persist oauth params and redirect to login
    if (!userStore.loggedIn) {
      // FIXME: Verify the oauthParams are valid before saving do not redirect if they are not
      LocalStorage.set('postLoginParams', JSON.stringify(oauthParams));
      setIsProcessing(true);
      history.replace('/login');

      return;
    }

    // when logged in, redirect to the authorization URL
    if (userStore.provider) {
      setIsProcessing(true);
      window.location.replace(buildCognitoOauthUrl(oauthParams as OAuthParams, userStore.provider));
    } else {
      toggleShow();
    }
  };

  if (error) {
    return (
      <McpAuthorizationCard
        content={
          <>
            <H4 style={{ textAlign: 'center' }}>The MCP client is requesting access</H4>
            <Alert type="error" description={error} message="OAuth Error" />
          </>
        }
        actions={
          <Link to="/" rel="noopener noreferrer">
            <Button size="middle" type="default">
              Cancel
            </Button>
          </Link>
        }
      />
    );
  }

  if (isProcessing) {
    return (
      <McpAuthorizationCard
        content={
          <>
            <H4 style={{ textAlign: 'center' }}>The MCP client is requesting access</H4>
            <Flex
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              flexGrow
              gap="1rem"
            >
              <Loader type="spin" size={36} />
              <SmallText style={{ textAlign: 'center', fontSize: '14px' }}>
                Completing authorization…
              </SmallText>
            </Flex>
          </>
        }
        actions={null}
      />
    );
  }

  return (
    <>
      {modalPlaceholder}
      <McpAuthorizationCard
        content={
          <>
            <H4 style={{ textAlign: 'center' }}>The MCP client is requesting access</H4>
            <Flex flexDirection="column" justifyContent="center" flexGrow>
              <SmallText style={{ textAlign: 'center', fontSize: '14px' }}>
                This MCP client is requesting to be authorized.
              </SmallText>
            </Flex>
          </>
        }
        actions={
          <>
            <Link to="/" rel="noopener noreferrer">
              <Button size="middle" type="default">
                Cancel
              </Button>
            </Link>
            <Button size="middle" type="primary" onClick={handleAuthorize}>
              Authorize
            </Button>
          </>
        }
      />
    </>
  );
};
